/**
 * Stable bundle grouping for workspace-aware pickers. Accepts both API and UI identifier
 * conventions and sends bundles without an active project to a final No project group.
 *
 * @module apps/web/src/lib/bundleGroups
 */

/** Minimal project projection accepted by grouping helpers across UI and API field naming. */
type ProjectLike = {
  /** Canonical identifier when the record uses the normalized shape. */
  id?: string | null;
  /** Retained snake-case project link or identifier accepted by grouping adapters. */
  project_id?: string | null;
  /** Display label used for the bundle or project selector. */
  name?: string | null;
  /** Optional lifecycle status used to omit archived projects. */
  status?: string | null;
};

/** Minimal bundle projection carrying its own identity, project association, and status for grouping. */
type BundleLike = {
  /** Canonical identifier when the record uses the normalized shape. */
  id?: string | null;
  /** Retained snake-case bundle identifier accepted by grouping adapters. */
  bundle_id?: string | null;
  /** Display label used for the bundle or project selector. */
  name?: string | null;
  /** Normalized parent-project identifier for grouping bundles. */
  projectId?: string | null;
  /** Retained snake-case project link or identifier accepted by grouping adapters. */
  project_id?: string | null;
};

/**
 * Picker group retaining the original bundle objects alongside an optional project ID and display
 * label.
 */
export interface BundleProjectGroup<TBundle> {
  /** Stable rendered group identity, including the ungrouped fallback. */
  id: string;
  /** Project heading displayed above the group's bundle choices. */
  label: string;
  /** Associated project identifier, or null for the ungrouped section. */
  projectId: string | null;
  /** Bundle records assigned to this project group. */
  bundles: TBundle[];
}

/** Sentinel grouping key for bundles whose project association is absent or unresolved. */
const NO_PROJECT_ID = '__no_project__';

/**
 * Group bundles under active projects in input project order, preserving bundle order within each
 * group. Missing or archived project references fall into the final No project group.
 */
export function groupBundlesByProject<TBundle extends BundleLike>(
  bundles: TBundle[],
  projects: ProjectLike[]
): BundleProjectGroup<TBundle>[] {
  const activeProjects = projects.filter(project => project.status !== 'archived');
  const projectById = new Map<string, ProjectLike>();
  const projectOrder = new Map<string, number>();
  const groups = new Map<string, BundleProjectGroup<TBundle>>();

  activeProjects.forEach((project, index) => {
    const id = projectEntityId(project);
    if (!id) return;
    projectById.set(id, project);
    projectOrder.set(id, index);
  });

  for (const bundle of bundles) {
    const projectId = bundleProjectId(bundle);
    const project = projectId ? projectById.get(projectId) : null;
    const groupId = project ? `project:${projectId}` : NO_PROJECT_ID;
    const fallbackLabel = project ? 'Untitled project' : 'No project';

    if (!groups.has(groupId)) {
      groups.set(groupId, {
        id: groupId,
        label: project?.name?.trim() || fallbackLabel,
        projectId: project ? projectId : null,
        bundles: [],
      });
    }

    groups.get(groupId)!.bundles.push(bundle);
  }

  return Array.from(groups.values()).sort((left, right) => {
    const leftOrder = left.projectId ? projectOrder.get(left.projectId) ?? Number.MAX_SAFE_INTEGER - 1 : Number.MAX_SAFE_INTEGER;
    const rightOrder = right.projectId ? projectOrder.get(right.projectId) ?? Number.MAX_SAFE_INTEGER - 1 : Number.MAX_SAFE_INTEGER;
    if (leftOrder !== rightOrder) return leftOrder - rightOrder;
    return left.label.localeCompare(right.label);
  });
}

/** Read either UI id or wire project_id and normalize it to a trimmed grouping key. */
function projectEntityId(project: ProjectLike): string {
  return String(project.id || project.project_id || '').trim();
}

/** Read either UI projectId or wire project_id; an empty result belongs to the unassigned-project group. */
function bundleProjectId(bundle: BundleLike): string {
  return String(bundle.projectId || bundle.project_id || '').trim();
}
